/**
 * Phieu cuoc.
 *
 * May tinh: cot dinh ben phai. Di dong: tam truot tu duoi len, kem huy hieu noi
 * hien so chang — ngon tay cai voi toi de hon va khong che mat noi dung.
 *
 * Ba quy tac nghiep vu phai hien ra man hinh, khong duoc giau:
 *   SPT-R13  Tran chi tra moi phieu, hien TRUOC khi bam dat cuoc.
 *   SPT-R25  May chu co the chi nhan MOT PHAN tien cuoc — phai hoi y kien.
 *   SPT-R21  Cuoc truc tiep co do tre chap nhan; noi ro de nguoi choi khong bat ngo.
 */

import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, newIdempotencyKey, type BetslipRequest } from '../lib/api';
import { useBetslip, combineOddsBp, type BetslipLeg } from '../lib/betslip';
import { useSession, usePrimaryWallet } from '../lib/session';
import { formatMoney, formatOdds, parseAmount } from '../lib/money';
import { Button, ErrorList } from './ui';
import styles from './BetSlip.module.css';

const QUICK_STAKES = [10, 50, 100, 500];

export function BetSlip(): JSX.Element | null {
  const slip = useBetslip();
  const { isAuthenticated, me, refresh } = useSession();
  const wallet = usePrimaryWallet();
  const queryClient = useQueryClient();

  const [placed, setPlaced] = useState<{ betId: string; partial: boolean } | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  const currency = me?.currency ?? 'USD';

  /**
   * Khoa bat bien sinh MOT LAN cho mot y dinh dat cuoc.
   * Doi phieu (them/bo chang, doi tien cuoc) la mot y dinh MOI nen sinh khoa moi;
   * bam hai lan tren cung mot phieu thi dung lai khoa cu, va may chu chi tao mot phieu.
   */
  const slipSignature = useMemo(
    () =>
      [
        slip.mode,
        slip.stake,
        ...slip.legs.map((l) => `${l.selectionId}:${slip.singleStakes[l.selectionId] ?? ''}`),
      ].join('|'),
    [slip.mode, slip.stake, slip.legs, slip.singleStakes],
  );

  useEffect(() => {
    setIdempotencyKey(newIdempotencyKey());
    setPlaced(null);
  }, [slipSignature]);

  // Cuoc don nhieu chang duoc gui thanh nhieu phieu; man hinh nay kiem tra tung phieu mot.
  const isMulti = slip.mode === 'MULTI' && slip.legs.length > 1;
  const stakeText = isMulti ? slip.stake : (slip.legs[0] ? slip.singleStakes[slip.legs[0].selectionId] ?? '' : '');

  const stakeMinor = useMemo(() => {
    try {
      return stakeText ? parseAmount(stakeText, currency) : 0n;
    } catch {
      return 0n;
    }
  }, [stakeText, currency]);

  const validateBody: BetslipRequest | null = useMemo(() => {
    if (slip.legs.length === 0 || stakeMinor <= 0n) return null;
    const legs = isMulti ? slip.legs : slip.legs.slice(0, 1);
    return {
      bet_type: isMulti ? 'MULTI' : 'SINGLE',
      legs: legs.map((l) => ({ selection_id: l.selectionId, expected_odds_bp: l.capturedOddsBp })),
      stake_minor: stakeMinor.toString(),
      odds_change_policy: slip.oddsPolicy,
    };
  }, [slip.legs, slip.oddsPolicy, isMulti, stakeMinor]);

  // Goi kiem tra o may chu: tra ve muc nhan toi da, tran chi tra va moi vuong mac.
  const validation = useQuery({
    queryKey: ['bet-validate', validateBody],
    queryFn: () => api.bets.validate(validateBody!),
    enabled: isAuthenticated && validateBody !== null,
    retry: false,
    staleTime: 5_000,
  });

  const place = useMutation({
    mutationFn: async (acceptPartial: boolean) =>
      api.bets.place({ ...validateBody!, accept_partial: acceptPartial }, idempotencyKey),
    onSuccess: (result) => {
      setPlaced({ betId: result.bet_id, partial: result.partially_accepted });
      slip.clear();
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['bets'] });
    },
  });

  if (slip.legs.length === 0 && !placed) return null;

  const localOdds = combineOddsBp(isMulti ? slip.legs : slip.legs.slice(0, 1));
  const blockers = validation.data?.blockers ?? [];
  const placeError = place.error instanceof ApiError ? place.error : null;
  const partialOffer =
    validation.data && validation.data.accepted_stake.amount_minor !== validation.data.requested_stake.amount_minor;

  const insufficient =
    wallet !== null && stakeMinor > BigInt(wallet.available.amount_minor) + BigInt(wallet.bonus.amount_minor);

  return (
    <>
      {/* Huy hieu noi tren di dong: cho biet co bao nhieu chang dang cho */}
      <button
        className={styles.fab}
        onClick={() => slip.setOpen(!slip.open)}
        aria-expanded={slip.open}
      >
        <span className={styles.fabCount}>{slip.count}</span>
        <span>Phiếu cược</span>
      </button>

      <aside
        className={[styles.panel, slip.open ? styles.panelOpen : ''].filter(Boolean).join(' ')}
        aria-label="Phiếu cược"
      >
        <header className={styles.header}>
          <h2 className={styles.title}>Phiếu cược</h2>
          <div className={styles.headerActions}>
            {slip.legs.length > 0 && (
              <button className={styles.clear} onClick={slip.clear}>Xóa hết</button>
            )}
            <button
              className={styles.close}
              onClick={() => slip.setOpen(false)}
              aria-label="Đóng phiếu cược"
            >
              ×
            </button>
          </div>
        </header>

        {placed ? (
          <div className={styles.success} role="status">
            <p className={styles.successTitle}>Đã đặt cược</p>
            {placed.partial && (
              <p className={styles.successNote}>
                Máy chủ chỉ nhận một phần tiền cược do chạm giới hạn nhận cược.
              </p>
            )}
            <Button variant="secondary" fullWidth onClick={() => setPlaced(null)}>
              Đặt cược tiếp
            </Button>
          </div>
        ) : (
          <>
            {slip.legs.length > 1 && (
              <div className={styles.modes} role="tablist">
                <button
                  role="tab"
                  aria-selected={slip.mode === 'SINGLE'}
                  className={[styles.mode, slip.mode === 'SINGLE' ? styles.modeActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => slip.setMode('SINGLE')}
                >
                  Cược đơn
                </button>
                <button
                  role="tab"
                  aria-selected={slip.mode === 'MULTI'}
                  className={[styles.mode, slip.mode === 'MULTI' ? styles.modeActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => slip.setMode('MULTI')}
                >
                  Xâu {slip.legs.length}
                </button>
              </div>
            )}

            <ul className={styles.legs}>
              {slip.legs.map((leg) => (
                <LegRow
                  key={leg.selectionId}
                  leg={leg}
                  serverOddsBp={
                    validation.data?.legs.find((l) => l.selection_id === leg.selectionId)
                      ?.server_odds_bp
                  }
                  moved={
                    validation.data?.legs.find((l) => l.selection_id === leg.selectionId)?.odds_moved
                  }
                  showStake={!isMulti}
                  stake={slip.singleStakes[leg.selectionId] ?? ''}
                  currency={currency}
                  onStake={(v) => slip.setSingleStake(leg.selectionId, v)}
                  onRemove={() => slip.remove(leg.selectionId)}
                />
              ))}
            </ul>

            {isMulti && (
              <div className={styles.stakeBlock}>
                <div className={styles.stakeRow}>
                  <label className={styles.stakeLabel} htmlFor="multi-stake">Tiền cược</label>
                  <input
                    id="multi-stake"
                    className={`numeric ${styles.stakeInput}`}
                    inputMode="decimal"
                    placeholder="0"
                    value={slip.stake}
                    onChange={(e) => slip.setStake(e.target.value)}
                  />
                </div>
                <div className={styles.quick}>
                  {QUICK_STAKES.map((q) => (
                    <button key={q} className={styles.quickBtn} onClick={() => slip.setStake(String(q))}>
                      +{q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.summary}>
              <Row label="Tỷ lệ tổng">
                <span className="numeric">
                  {validation.data?.total_odds ?? (localOdds > 0n ? formatOdds(localOdds) : '—')}
                </span>
              </Row>

              {validation.data && (
                <>
                  <Row label="Tiền thắng dự kiến" strong>
                    <span className="numeric">
                      {formatMoney(validation.data.potential_win)} {currency}
                    </span>
                  </Row>
                  {/* SPT-R13: tran chi tra PHAI hien truoc khi xac nhan */}
                  <Row label="Trần chi trả mỗi phiếu" muted>
                    <span className="numeric">
                      {formatMoney(validation.data.max_payout)} {currency}
                    </span>
                  </Row>
                  {validation.data.live_delay_ms > 0 && (
                    <Row label="Độ trễ chấp nhận" muted>
                      {(validation.data.live_delay_ms / 1000).toFixed(0)} giây
                    </Row>
                  )}
                </>
              )}
            </div>

            {insufficient && (
              <ErrorList messages={['Số dư không đủ cho phiếu cược này.']} />
            )}

            {blockers.length > 0 && <ErrorList messages={blockers.map((b) => b.message)} />}

            {placeError && <ErrorList messages={placeError.messages} />}

            {/* SPT-R25: hoi y kien truoc khi nhan mot phan, khong tu quyet dinh thay */}
            {partialOffer && validation.data && (
              <div className={styles.partial} role="alert">
                <p>
                  Chỉ nhận được{' '}
                  <strong className="numeric">
                    {formatMoney(validation.data.accepted_stake)} {currency}
                  </strong>{' '}
                  trên tổng{' '}
                  <span className="numeric">
                    {formatMoney(validation.data.requested_stake)} {currency}
                  </span>{' '}
                  do chạm giới hạn nhận cược.
                </p>
                <Button size="sm" fullWidth loading={place.isPending} onClick={() => place.mutate(true)}>
                  Chấp nhận một phần
                </Button>
              </div>
            )}

            <Button
              fullWidth
              size="lg"
              loading={place.isPending}
              disabled={
                !isAuthenticated ||
                stakeMinor <= 0n ||
                insufficient ||
                blockers.length > 0 ||
                partialOffer ||
                validation.isLoading
              }
              onClick={() => place.mutate(false)}
            >
              {isAuthenticated ? 'Đặt cược' : 'Đăng nhập để cược'}
            </Button>
          </>
        )}
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------

function Row({
  label,
  children,
  strong,
  muted,
}: {
  label: string;
  children: ReactNode;
  strong?: boolean;
  muted?: boolean;
}): JSX.Element {
  return (
    <div className={[styles.row, muted ? styles.rowMuted : ''].filter(Boolean).join(' ')}>
      <span>{label}</span>
      <span className={strong ? styles.rowStrong : undefined}>{children}</span>
    </div>
  );
}

function LegRow({
  leg,
  serverOddsBp,
  moved,
  showStake,
  stake,
  currency,
  onStake,
  onRemove,
}: {
  leg: BetslipLeg;
  serverOddsBp: number | undefined;
  moved: 'NONE' | 'BETTER' | 'WORSE' | undefined;
  showStake: boolean;
  stake: string;
  currency: string;
  onStake: (value: string) => void;
  onRemove: () => void;
}): JSX.Element {
  const displayOdds = serverOddsBp ?? leg.capturedOddsBp;

  return (
    <li className={styles.leg}>
      <div className={styles.legTop}>
        <div className={styles.legInfo}>
          <span className={styles.legSelection}>{leg.selectionName}</span>
          <span className={styles.legMarket}>{leg.marketLabel}</span>
          <span className={styles.legEvent}>
            {leg.isLive && <span className={styles.liveTag}>TRỰC TIẾP</span>}
            {leg.eventLabel}
          </span>
        </div>

        <div className={styles.legRight}>
          <span
            className={[
              'numeric',
              styles.legOdds,
              moved === 'BETTER' ? styles.oddsUp : '',
              moved === 'WORSE' ? styles.oddsDown : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {formatOdds(displayOdds)}
          </span>
          <button className={styles.legRemove} onClick={onRemove} aria-label="Bỏ lựa chọn này">
            ×
          </button>
        </div>
      </div>

      {/* SPT-R22: ty le tot len thi may chu tu nhan, chi bao cho nguoi choi biet */}
      {moved === 'BETTER' && (
        <p className={styles.oddsNoteUp}>Tỷ lệ đã tăng — áp dụng tỷ lệ mới.</p>
      )}
      {moved === 'WORSE' && (
        <p className={styles.oddsNoteDown}>
          Tỷ lệ đã giảm từ {formatOdds(leg.capturedOddsBp)} xuống {formatOdds(displayOdds)}.
        </p>
      )}

      {showStake && (
        <div className={styles.legStake}>
          <input
            className={`numeric ${styles.legStakeInput}`}
            inputMode="decimal"
            placeholder="0"
            value={stake}
            onChange={(e) => onStake(e.target.value)}
            aria-label={`Tiền cược cho ${leg.selectionName}`}
          />
          <span className={styles.legCurrency}>{currency}</span>
        </div>
      )}
    </li>
  );
}
